import {
  AlertCircle,
  CheckCircle2,
  CheckSquare,
  FileSpreadsheet,
  FileText,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Save,
  Search,
  X,
} from "lucide-react";
import type { InvoiceAiSaveState } from "../hooks/useInvoiceAiAudit";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/shared/lib/cn";
import { inputClass } from "./FormField";
import { TariffActAutocomplete } from "./TariffActAutocomplete";
import {
  fetchAuditorHospitalActs,
  type AuditorHospitalMedicalAct,
  type InvoiceAiActCorrection,
  type InvoiceAiActReview,
  type InvoiceAiAnalysisRow,
} from "../api/auditorApi";
import {
  analysisRowAmounts,
  computeAiMoneySummary,
  formatManualMeta,
  isSynthesisRow,
  obsRowClassName,
  parseManualMeta,
  roundMoney,
  rowIsAnomalyForReview,
  sortAnalysisTable,
} from "../lib/invoiceAiRowAmounts";

type FilterKey =
  | "anomalies"
  | "surcharge"
  | "sous_facturation"
  | "non_tarife"
  | "sans_proforma"
  | "ok"
  | "all";

type InvoiceAiCorrectionDialogProps = {
  open: boolean;
  invoiceId: number;
  hospitalId?: number | null;
  table: InvoiceAiAnalysisRow[];
  corrections: Record<number, InvoiceAiActCorrection>;
  reviews: Record<number, InvoiceAiActReview>;
  currency: string;
  exchangeRateCdf: number;
  withReviews: boolean;
  saving: boolean;
  reanalyzing: boolean;
  dirty: boolean;
  saveState: InvoiceAiSaveState;
  /** Message d'erreur renvoyé par le serveur lors du dernier enregistrement. */
  saveError?: string | null;
  billedTotal?: number;
  onClose: () => void;
  onChange: (actIndex: number, patch: Partial<InvoiceAiActCorrection>) => void;
  onReviewChange: (actIndex: number, patch: Partial<InvoiceAiActReview>) => void;
  onSaveAll: () => Promise<boolean>;
  onSaveAndReanalyze: () => Promise<unknown>;
  onOpenRefraction?: () => Promise<void>;
  onDownloadRefraction?: () => Promise<void>;
  onAddManualRow: (payload: {
    patient: string;
    date: string;
    actName: string;
    qty: number;
    unitPrice: number;
  }) => void;
  onRemoveManualRow: (actIndex: number) => void;
  onRetainedPreview?: (total: number) => void;
};

const FILTERS: Array<{ key: FilterKey; label: string; className: string }> = [
  { key: "anomalies", label: "Anomalies", className: "border-red-300 text-red-700" },
  { key: "sans_proforma", label: "Sans proforma", className: "border-red-300 text-red-700" },
  { key: "surcharge", label: "Surcharge", className: "border-red-300 text-red-700" },
  { key: "sous_facturation", label: "Sous-fact.", className: "border-amber-300 text-amber-800" },
  { key: "non_tarife", label: "Non tarifé", className: "border-border text-muted-foreground" },
  { key: "ok", label: "OK", className: "border-green-300 text-green-700" },
  { key: "all", label: "Tous", className: "border-border text-foreground" },
];

function obsLabel(type: string): string {
  switch (type) {
    case "surcharge":
      return "Surcharge";
    case "sous_facturation":
      return "Sous-fact.";
    case "non_tarifé":
      return "Non tarifé";
    case "sans_proforma":
      return "Sans proforma";
    case "synthese":
      return "Synthèse";
    default:
      return "OK";
  }
}

function SaveIndicator({
  state,
  dirty,
  saving,
}: {
  state: InvoiceAiSaveState;
  dirty: boolean;
  saving: boolean;
}) {
  if (saving) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted">
        <Loader2 size={12} className="animate-spin" /> Enregistrement…
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
        <AlertCircle size={12} /> Non enregistré — cliquez « Enregistrer »
      </span>
    );
  }
  if (dirty) {
    return <span className="text-xs text-amber-700">Modifications en attente d&apos;enregistrement…</span>;
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700">
        <CheckCircle2 size={12} /> Tout est enregistré
      </span>
    );
  }
  return null;
}

function formatAmount(n: number, decimals: number, suffix: string): string {
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}${suffix}`;
}

export function InvoiceAiCorrectionDialog({
  open,
  invoiceId,
  hospitalId,
  table,
  corrections,
  reviews,
  currency,
  exchangeRateCdf,
  withReviews,
  saving,
  reanalyzing,
  dirty,
  saveState,
  saveError,
  billedTotal,
  onClose,
  onChange,
  onReviewChange,
  onSaveAll,
  onSaveAndReanalyze,
  onOpenRefraction,
  onDownloadRefraction,
  onAddManualRow,
  onRemoveManualRow,
  onRetainedPreview,
}: InvoiceAiCorrectionDialogProps) {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("anomalies");
  const [tariffs, setTariffs] = useState<AuditorHospitalMedicalAct[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [highlightPrice, setHighlightPrice] = useState<number | null>(null);
  const [manualPatient, setManualPatient] = useState("");
  const [manualDate, setManualDate] = useState("");
  const [manualAct, setManualAct] = useState("");
  const [manualQty, setManualQty] = useState(1);
  const [manualPrice, setManualPrice] = useState<number | "">("");

  const decimals = currency === "CDF" ? 0 : 2;
  const step = currency === "CDF" ? 1 : 0.01;
  const suffix = currency === "CDF" ? " FC" : " USD";

  useEffect(() => {
    if (!open || !hospitalId) return;
    void (async () => {
      try {
        const result = await fetchAuditorHospitalActs(hospitalId);
        setTariffs(result.medical_acts);
      } catch {
        setTariffs([]);
      }
    })();
  }, [open, hospitalId]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setActiveFilter("anomalies");
      setMessage(null);
      setManualPatient("");
      setManualDate("");
      setManualAct("");
      setManualQty(1);
      setManualPrice("");
    }
  }, [open]);

  const rows = useMemo(() => {
    const sorted = sortAnalysisTable(table);
    return sorted.map((row, idx) => ({
      row,
      idx,
      actIndex: row.act_orig_index ?? idx,
      typeObs: isSynthesisRow(row) ? "synthese" : (row.type_obs ?? "ok"),
      searchHay: `${row.act_name ?? ""} ${row.patient ?? ""}`.toLowerCase(),
      isManual: !!row.is_manual,
      isSynthesis: isSynthesisRow(row),
    }));
  }, [table]);

  const filterCounts = useMemo(() => {
    const counts: Record<FilterKey, number> = {
      anomalies: 0,
      surcharge: 0,
      sous_facturation: 0,
      non_tarife: 0,
      sans_proforma: 0,
      ok: 0,
      all: rows.filter((r) => !r.isSynthesis).length,
    };
    for (const item of rows) {
      if (item.isSynthesis) continue;
      const status = reviews[item.actIndex]?.review_status;
      const isAnomaly = rowIsAnomalyForReview(item.typeObs, status, withReviews);
      if (isAnomaly) counts.anomalies++;
      if (item.typeObs === "ok" || (withReviews && status === "approved")) {
        counts.ok++;
      } else if (item.typeObs === "non_tarifé" && isAnomaly) {
        counts.non_tarife++;
      } else if (isAnomaly && item.typeObs in counts) {
        counts[item.typeObs as FilterKey]++;
      }
    }
    return counts;
  }, [reviews, rows, withReviews]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((item) => {
      if (item.isSynthesis) return activeFilter === "all" && (!q || item.searchHay.includes(q));
      if (q && !item.searchHay.includes(q)) return false;
      const status = reviews[item.actIndex]?.review_status;
      const isAnomaly = rowIsAnomalyForReview(item.typeObs, status, withReviews);
      switch (activeFilter) {
        case "anomalies":
          return isAnomaly;
        case "ok":
          return item.typeObs === "ok" || (withReviews && status === "approved");
        case "non_tarife":
          return item.typeObs === "non_tarifé" && isAnomaly;
        case "all":
          return true;
        default:
          return item.typeObs === activeFilter && isAnomaly;
      }
    });
  }, [activeFilter, reviews, rows, search, withReviews]);

  const reviewTotals = useMemo(() => {
    let approved = 0;
    let rejected = 0;
    let pending = 0;
    let totalRetenu = 0;
    for (const item of rows) {
      if (item.isSynthesis) continue;
      const status = reviews[item.actIndex]?.review_status ?? "pending";
      if (status === "approved") {
        approved++;
        const v = Number(reviews[item.actIndex]?.montant_retenu);
        if (!Number.isNaN(v)) totalRetenu += v;
      } else if (status === "rejected") {
        rejected++;
      } else {
        pending++;
      }
    }
    return { approved, rejected, pending, totalRetenu: roundMoney(totalRetenu, currency) };
  }, [currency, reviews, rows]);

  const moneySummary = useMemo(
    () =>
      computeAiMoneySummary({
        rows: table,
        reviews,
        currency,
        exchangeRateCdf,
        billedTotal,
        retainedOverride: reviewTotals.totalRetenu,
      }),
    [billedTotal, currency, exchangeRateCdf, reviewTotals.totalRetenu, reviews, table],
  );

  useEffect(() => {
    if (withReviews && onRetainedPreview) {
      onRetainedPreview(reviewTotals.totalRetenu);
    }
  }, [onRetainedPreview, reviewTotals.totalRetenu, withReviews]);

  if (!open) return null;

  const busy = saving || reanalyzing;
  const refractionReady = withReviews && !dirty && reviewTotals.pending === 0;

  const handleClose = () => {
    if (!dirty) {
      onClose();
      return;
    }
    void (async () => {
      const ok = await onSaveAll();
      if (ok || window.confirm("L'enregistrement a échoué. Fermer quand même et perdre les modifications ?")) {
        onClose();
      } else {
        setMessage("Enregistrement impossible — vérifiez le message d'erreur puis réessayez.");
      }
    })();
  };

  const handleReanalyze = () => {
    const ok = window.confirm(
      "Ré-analyser recalcule les anomalies à partir de vos corrections (grille tarifaire, proformas).\n\n" +
        "Vos validations sont enregistrées d'abord et conservées, mais le statut de certaines lignes peut changer.\n\nContinuer ?",
    );
    if (!ok) return;
    void (async () => {
      const result = await onSaveAndReanalyze();
      if (result && typeof result === "object" && "conformity_score" in result) {
        const score = Number((result as { conformity_score: number }).conformity_score);
        setMessage(`Ré-analyse terminée — score ${score.toFixed(1)} %`);
      } else if (result) {
        setMessage("Ré-analyse terminée.");
      }
    })();
  };

  const runRefraction = (action?: () => Promise<void>) => {
    if (!action) return;
    void (async () => {
      try {
        await action();
      } catch (err) {
        setMessage(err instanceof Error ? err.message : "Fiche de réfraction indisponible.");
      }
    })();
  };

  const handleNoteChange = (
    actIndex: number,
    note: string,
    isManual: boolean,
    row: InvoiceAiAnalysisRow,
  ) => {
    onReviewChange(actIndex, { review_note: note });
    if (isManual) {
      onChange(actIndex, {
        note_correction: formatManualMeta(row.patient ?? "", row.date ?? ""),
      });
    } else {
      onChange(actIndex, { note_correction: note });
    }
  };

  const approveVisibleLines = () => {
    let count = 0;
    for (const item of visibleRows) {
      if (item.isSynthesis) continue;
      const review = reviews[item.actIndex];
      const status = review?.review_status ?? "pending";
      if (status === "approved" || status === "rejected") continue;

      const amounts = analysisRowAmounts(item.row, currency, exchangeRateCdf);
      const corr = corrections[item.actIndex];
      const qty = Math.max(1, corr?.corrected_qty ?? item.row.qty ?? 1);
      const pu = corr?.corrected_price ?? amounts.unit;
      const grille = amounts.grille;
      let retained = amounts.total;
      if (pu > 0) retained = roundMoney(pu * qty, currency);
      else if (grille > 0) retained = roundMoney(grille * qty, currency);

      onReviewChange(item.actIndex, {
        review_status: "approved",
        montant_retenu: retained,
        montant_facture: amounts.total,
        approve_without_proforma: item.typeObs === "sans_proforma" ? 1 : review?.approve_without_proforma,
      });
      count++;
    }
    setMessage(
      count > 0
        ? `${count} ligne(s) approuvée(s) — enregistrement automatique en cours.`
        : "Aucune ligne à approuver dans le filtre actuel.",
    );
  };

  const handleReviewStatusChange = (actIndex: number, status: InvoiceAiActReview["review_status"]) => {
    const item = rows.find((r) => r.actIndex === actIndex);
    if (!item || item.isSynthesis) return;
    const amounts = analysisRowAmounts(item.row, currency, exchangeRateCdf);
    const corr = corrections[actIndex];
    const qty = Math.max(1, corr?.corrected_qty ?? item.row.qty ?? 1);
    const pu = corr?.corrected_price ?? amounts.unit;

    if (status === "approved") {
      const retained =
        pu > 0 ? roundMoney(pu * qty, currency) : roundMoney(amounts.total, currency);
      onReviewChange(actIndex, {
        review_status: status,
        montant_retenu: retained,
        montant_facture: amounts.total,
      });
    } else {
      onReviewChange(actIndex, {
        review_status: status,
        montant_retenu: undefined,
      });
    }
  };

  const submitManualRow = () => {
    if (!manualPatient.trim() || !manualAct.trim()) {
      setMessage("Patient et acte requis pour ajouter une ligne.");
      return;
    }
    const price = manualPrice === "" ? 0 : Number(manualPrice);
    onAddManualRow({
      patient: manualPatient.trim(),
      date: manualDate,
      actName: manualAct.trim(),
      qty: manualQty,
      unitPrice: price,
    });
    setManualPatient("");
    setManualDate("");
    setManualAct("");
    setManualQty(1);
    setManualPrice("");
    setMessage("Ligne manuelle ajoutée — enregistrement automatique en cours.");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-2 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="corr-dialog-title"
        className="flex max-h-[95vh] w-full max-w-[96rem] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
          <div>
            <h2 id="corr-dialog-title" className="text-base font-semibold text-foreground">
              Correction des actes — Facture #{invoiceId}
            </h2>
            <p className="mt-1 text-xs text-muted">
              {filterCounts.anomalies} anomalie(s) · {visibleRows.length} ligne(s) affichée(s)
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="rounded-lg p-1.5 text-muted hover:bg-card-muted disabled:opacity-60"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 border-b border-border bg-card-muted/30 px-4 py-3 sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative max-w-md flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher acte, patient…"
                className={cn(inputClass, "pl-9 text-sm")}
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setActiveFilter(filter.key)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[11px] font-medium",
                    filter.className,
                    activeFilter === filter.key && "bg-card shadow-sm ring-1 ring-primary/30",
                  )}
                >
                  {filter.label} ({filterCounts[filter.key] ?? 0})
                </button>
              ))}
            </div>
          </div>

          {withReviews && (
            <div className="space-y-2 border-t border-border pt-3 text-xs">
              <div className="grid gap-2 sm:grid-cols-3">
                <p className="rounded-lg border border-border bg-card px-3 py-2">
                  Total facturé
                  <strong className="mt-0.5 block text-sm text-foreground">
                    {formatAmount(moneySummary.billedTotal, decimals, suffix)}
                  </strong>
                </p>
                <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-green-800">
                  Montant retenu
                  <strong className="mt-0.5 block text-sm">
                    {formatAmount(moneySummary.retainedTotal, decimals, suffix)}
                  </strong>
                </p>
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                  Montant réfracté
                  <strong className="mt-0.5 block text-sm">
                    {formatAmount(moneySummary.refractedTotal, decimals, suffix)}
                  </strong>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span>
                  <strong>{reviewTotals.approved}</strong> approuvée(s)
                </span>
                <span>
                  <strong>{reviewTotals.rejected}</strong> rejetée(s)
                </span>
                <span>
                  <strong>{reviewTotals.pending}</strong> en attente
                </span>
                <button
                  type="button"
                  onClick={approveVisibleLines}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-green-600 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50"
                >
                  <CheckSquare size={14} />
                  Approuver les lignes visibles
                </button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-sky-200 bg-sky-50/80 p-3">
            <p className="mb-2 text-xs font-semibold text-sky-900">+ Ajouter un patient / acte manquant</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[1.2fr_0.8fr_1.2fr_0.4fr_0.6fr_auto] lg:items-end">
              <label className="block text-[10px] uppercase text-muted">
                Patient (bénéficiaire)
                <input
                  type="text"
                  value={manualPatient}
                  onChange={(e) => setManualPatient(e.target.value)}
                  placeholder="Nom du bénéficiaire…"
                  className={cn(inputClass, "mt-0.5 text-xs")}
                />
              </label>
              <label className="block text-[10px] uppercase text-muted">
                Date
                <input
                  type="date"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  className={cn(inputClass, "mt-0.5 text-xs")}
                />
              </label>
              <label className="block text-[10px] uppercase text-muted">
                Acte
                <input
                  type="text"
                  value={manualAct}
                  onChange={(e) => setManualAct(e.target.value)}
                  placeholder="Libellé ou grille…"
                  className={cn(inputClass, "mt-0.5 text-xs")}
                />
              </label>
              <label className="block text-[10px] uppercase text-muted">
                Qté
                <input
                  type="number"
                  min={1}
                  value={manualQty}
                  onChange={(e) => setManualQty(Math.max(1, Number(e.target.value)))}
                  className={cn(inputClass, "mt-0.5 text-xs")}
                />
              </label>
              <label className="block text-[10px] uppercase text-muted">
                PU{suffix}
                <input
                  type="number"
                  min={0}
                  step={step}
                  value={manualPrice}
                  onChange={(e) =>
                    setManualPrice(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  className={cn(inputClass, "mt-0.5 text-xs")}
                />
              </label>
              <button
                type="button"
                onClick={submitManualRow}
                className="inline-flex h-9 items-center justify-center rounded-lg bg-primary px-3 text-white hover:bg-primary/90"
                title="Ajouter la ligne"
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="mt-2 text-[10px] text-muted">
              Lignes ajoutées manuellement : modifiables ou supprimables avant enregistrement.
            </p>
          </div>

          <p className="text-xs text-muted">
            Choisissez l&apos;acte grille pour remplir le prix. Cliquez sur le montant grille pour le copier.
            {withReviews && (
              <>
                {" "}
                <strong className="text-green-700">
                  Vos modifications sont enregistrées automatiquement.
                </strong>
              </>
            )}
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[1200px] text-xs">
            <thead className="sticky top-0 z-10 bg-card-muted/95 text-[10px] uppercase tracking-wide text-muted backdrop-blur">
              <tr className="border-b border-border">
                <th className="w-10 px-2 py-2 text-left">#</th>
                <th className="min-w-[12rem] px-2 py-2 text-left">Acte</th>
                <th className="min-w-[11rem] px-2 py-2 text-left">Acte grille</th>
                <th className="w-16 px-2 py-2 text-center">Qté</th>
                <th className="w-44 px-2 py-2 text-left">Montants</th>
                {withReviews && (
                  <>
                    <th className="w-24 px-2 py-2 text-left">Statut</th>
                    <th className="w-24 px-2 py-2 text-left">Retenu</th>
                    <th className="w-10 px-2 py-2 text-center" title="Sans proforma">
                      SP
                    </th>
                  </>
                )}
                <th className="min-w-[8rem] px-2 py-2 text-left">Note</th>
                <th className="w-12 px-2 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((item, displayIdx) => {
                const { row, actIndex, typeObs, isManual, isSynthesis } = item;
                const amounts = analysisRowAmounts(row, currency, exchangeRateCdf);
                const origName = row.act_name ?? "";
                const origQty = row.qty ?? 1;
                const origPrice = amounts.unit;
                const grilleUnit = amounts.grille;
                const corr = corrections[actIndex] ?? {
                  act_index: actIndex,
                  original_name: isManual ? "" : origName,
                  corrected_name: origName,
                  original_qty: isManual ? 0 : origQty,
                  corrected_qty: origQty,
                  original_price: isManual ? 0 : origPrice,
                  corrected_price: origPrice,
                };
                const review = reviews[actIndex];
                const reviewStatus = isSynthesis
                  ? "rejected"
                  : (review?.review_status ?? "pending");
                const isAnomaly = rowIsAnomalyForReview(typeObs, reviewStatus, withReviews);
                const noteValue = isSynthesis
                  ? "Ligne synthèse — non comptabilisée"
                  : (review?.review_note ?? "");

                const displayPrice = roundMoney(corr.corrected_price ?? origPrice, currency);

                if (isSynthesis) {
                  return (
                    <tr
                      key={actIndex}
                      className="border-b border-border bg-slate-100/80 align-top italic"
                    >
                      <td className="px-2 py-2 font-semibold text-muted">{displayIdx + 1}</td>
                      <td className="px-2 py-2" colSpan={withReviews ? 6 : 3}>
                        <p className="font-medium text-foreground not-italic">
                          {origName || "—"}
                          <span className="ml-1 rounded bg-slate-200 px-1 text-[9px] font-semibold uppercase text-slate-700">
                            synthèse · non comptée
                          </span>
                        </p>
                        <p className="mt-0.5 text-[11px] text-muted not-italic">
                          {formatAmount(amounts.total, decimals, suffix)} (exclu du retenu)
                        </p>
                      </td>
                      {withReviews && (
                        <>
                          <td className="px-2 py-2 not-italic">
                            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                              Exclue
                            </span>
                          </td>
                          <td className="px-2 py-2 not-italic text-muted">
                            {formatAmount(0, decimals, suffix)}
                          </td>
                          <td className="px-2 py-2 text-center text-muted">—</td>
                        </>
                      )}
                      <td className="px-2 py-2 text-[11px] text-muted not-italic">{noteValue}</td>
                      <td className="px-2 py-2 text-center text-muted">—</td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={actIndex}
                    className={cn("border-b border-border align-top", obsRowClassName(typeObs, isAnomaly))}
                  >
                    <td className="px-2 py-2 font-semibold text-muted">{displayIdx + 1}</td>
                    <td className="px-2 py-2">
                      <p className="font-medium text-foreground">
                        {origName || "—"}
                        {isManual && (
                          <span className="ml-1 rounded bg-sky-100 px-1 text-[9px] text-sky-800">
                            manuel
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted">
                        {row.patient ?? "—"}
                        {row.date
                          ? ` · ${new Date(row.date).toLocaleDateString("fr-FR")}`
                          : ""}
                      </p>
                      {!isManual && (
                        <span
                          className={cn(
                            "mt-1 inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                            isAnomaly ? "bg-amber-100 text-amber-900" : "bg-green-100 text-green-800",
                          )}
                        >
                          {obsLabel(typeObs)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <TariffActAutocomplete
                        hospitalId={hospitalId}
                        tariffs={tariffs}
                        currency={currency}
                        value={corr.corrected_name ?? origName}
                        onChange={(next) =>
                          onChange(actIndex, {
                            original_name: isManual ? "" : origName,
                            corrected_name: next,
                          })
                        }
                        onSelect={(act) =>
                          onChange(actIndex, {
                            original_name: isManual ? "" : origName,
                            corrected_name: act.label,
                            tariff_code: act.code,
                            corrected_price:
                              act.cost > 0 ? roundMoney(act.cost, currency) : origPrice,
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={corr.corrected_qty ?? origQty}
                        onChange={(e) =>
                          onChange(actIndex, {
                            original_qty: isManual ? 0 : origQty,
                            corrected_qty: Number(e.target.value),
                          })
                        }
                        className={cn(inputClass, "mx-auto w-14 text-center text-xs")}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="space-y-1">
                        <div className="text-[11px] text-muted">
                          Fact.{" "}
                          <strong className="text-foreground">
                            {formatAmount(origPrice, decimals, suffix)}
                          </strong>
                        </div>
                        {grilleUnit > 0 && (
                          <button
                            type="button"
                            className="text-left text-[11px] font-medium text-primary hover:underline"
                            onClick={() => {
                              onChange(actIndex, {
                                original_price: isManual ? 0 : origPrice,
                                corrected_price: roundMoney(grilleUnit, currency),
                              });
                              setHighlightPrice(actIndex);
                              window.setTimeout(() => setHighlightPrice(null), 1200);
                            }}
                          >
                            Grille {formatAmount(grilleUnit, decimals, suffix)} → copier
                          </button>
                        )}
                        <input
                          type="number"
                          min={0}
                          step={step}
                          value={displayPrice}
                          onChange={(e) =>
                            onChange(actIndex, {
                              original_price: isManual ? 0 : origPrice,
                              corrected_price: roundMoney(Number(e.target.value), currency),
                            })
                          }
                          className={cn(
                            inputClass,
                            "text-xs",
                            highlightPrice === actIndex && "bg-green-100 ring-1 ring-green-400",
                          )}
                          title="Prix unitaire corrigé"
                        />
                      </div>
                    </td>
                    {withReviews && (
                      <>
                        <td className="px-2 py-2">
                          <select
                            value={reviewStatus}
                            onChange={(e) =>
                              handleReviewStatusChange(
                                actIndex,
                                e.target.value as InvoiceAiActReview["review_status"],
                              )
                            }
                            className={cn(inputClass, "text-xs")}
                          >
                            <option value="pending">Attente</option>
                            <option value="approved">OK</option>
                            <option value="rejected">Rejet</option>
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min={0}
                            step={step}
                            value={review?.montant_retenu ?? ""}
                            disabled={reviewStatus !== "approved"}
                            placeholder="Total"
                            onChange={(e) =>
                              onReviewChange(actIndex, {
                                montant_retenu:
                                  e.target.value === ""
                                    ? undefined
                                    : roundMoney(Number(e.target.value), currency),
                              })
                            }
                            className={cn(inputClass, "text-xs")}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={!!review?.approve_without_proforma}
                            onChange={(e) =>
                              onReviewChange(actIndex, {
                                approve_without_proforma: e.target.checked ? 1 : 0,
                              })
                            }
                            className="h-4 w-4 rounded border-border"
                            title="Approuvé sans proforma"
                          />
                        </td>
                      </>
                    )}
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={noteValue}
                        onChange={(e) => handleNoteChange(actIndex, e.target.value, isManual, row)}
                        className={cn(inputClass, "text-xs")}
                        placeholder={isAnomaly ? "Motif…" : ""}
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      {isManual ? (
                        <button
                          type="button"
                          onClick={() => onRemoveManualRow(actIndex)}
                          className="rounded border border-red-300 p-1 text-red-600 hover:bg-red-50"
                          title="Supprimer la ligne"
                        >
                          <Minus size={14} />
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3 sm:px-5">
          <div className="mr-auto flex min-w-0 flex-col gap-0.5">
            <SaveIndicator state={saveState} dirty={dirty} saving={saving} />
            {message && <p className="text-xs text-foreground">{message}</p>}
            {saveState === "error" && saveError && (
              <p className="max-w-xl break-words text-xs text-red-700">{saveError}</p>
            )}
          </div>
          {withReviews && (
            <span className="hidden text-xs text-muted md:inline">
              Retenu :{" "}
              <strong className="text-green-700">
                {formatAmount(reviewTotals.totalRetenu, decimals, suffix)}
              </strong>
            </span>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={handleReanalyze}
            title="Recalcule les anomalies à partir des corrections — vos validations sont conservées"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-card-muted disabled:opacity-60"
          >
            {reanalyzing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Ré-analyser
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-card-muted disabled:opacity-60"
          >
            Fermer
          </button>
          {withReviews && onOpenRefraction && (
            <div className="inline-flex overflow-hidden rounded-lg border border-red-300">
              <button
                type="button"
                disabled={!refractionReady || busy}
                onClick={() => runRefraction(onOpenRefraction)}
                title={
                  refractionReady
                    ? "Ouvrir la fiche de réfraction (impression / PDF)"
                    : "Disponible quand toutes les lignes sont validées et enregistrées"
                }
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileText size={16} />
                Fiche de réfraction
              </button>
              <button
                type="button"
                disabled={!refractionReady || busy}
                onClick={() => runRefraction(onDownloadRefraction)}
                title="Télécharger la fiche de réfraction (Excel)"
                aria-label="Télécharger la fiche de réfraction en Excel"
                className="border-l border-red-300 px-2.5 py-2 text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileSpreadsheet size={16} />
              </button>
            </div>
          )}
          <button
            type="button"
            disabled={busy || !dirty}
            onClick={() => {
              void (async () => {
                const ok = await onSaveAll();
                setMessage(ok ? "Enregistré." : "Erreur lors de l'enregistrement.");
              })();
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
